import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  fromPromise,
  InMemoryCache,
  type Operation,
  split,
} from "@apollo/client";
import { RetryLink } from "@apollo/client/link/retry";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition, Observable } from "@apollo/client/utilities";
import { type ErrorResponse, onError } from "@apollo/link-error";
import { getLoginUrl } from "@hey-lady/shared/helpers/navigation";
import { isSubscriptions } from "@hey-lady/shared/helpers/utils";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { createClient } from "graphql-ws";
import React from "react";
import { userHostedEventsFieldsPolicy } from "./cache/hostedEventsCache";
import { notificationsFieldsPolicy } from "./cache/notificationsCache";
import { isPublicRoute } from "./publicRoutes";

/**
 * Types
 */
interface Props {
  children: JSX.Element[] | JSX.Element;
}

/**
 * Helpers
 */
const logout = (): void => {
  if (
    !isPublicRoute(window.location.pathname) &&
    !window.location.pathname.startsWith("/auth/login")
  ) {
    window.location.replace(getLoginUrl(window.location.pathname, window.location.search));
  }
};

const SCHEMA_RELOAD_KEY = "schemaMismatchReloadAt";
const SCHEMA_RELOAD_COOLDOWN_MS = 5 * 60 * 1000;

/**
 * A schema mismatch usually means a new web build is out. Reload at most once per cooldown, so a
 * mismatch that survives the reload (API behind the web app) can't loop.
 */
const reloadForSchemaMismatch = (): boolean => {
  try {
    const lastReloadAt = Number(window.sessionStorage.getItem(SCHEMA_RELOAD_KEY));
    if (lastReloadAt && Date.now() - lastReloadAt < SCHEMA_RELOAD_COOLDOWN_MS) {
      return false;
    }
    window.sessionStorage.setItem(SCHEMA_RELOAD_KEY, String(Date.now()));
  } catch {
    // Without storage we can't tell whether we already reloaded: don't risk a loop
    return false;
  }
  window.location.reload();
  return true;
};

/**
 * Links
 */
const ws = new GraphQLWsLink(
  createClient({
    shouldRetry() {
      return true;
    },
    url: import.meta.env.VITE_API_WSS_URL ?? "",
  }),
);

const http = createUploadLink({
  credentials: "include",
  uri: import.meta.env.VITE_API_URL,
  headers: { "Apollo-Require-Preflight": "true" },
});

const retryLink = new RetryLink();

let isRefreshing = false;
let pendingRequests: Array<() => void> = [];

const resolvePendingRequests = () => {
  pendingRequests.forEach((callback) => {
    callback();
  });
  pendingRequests = [];
};

function getNewToken() {
  return fetch(`${import.meta.env.VITE_OAUTH_URL}/refresh`, {
    method: "POST",
    credentials: "include",
  });
}

/**
 * Error handling
 *
 * Try to use refresh token
 * in case of error when users access token is expired
 * @link https://able.bio/AnasT/apollo-graphql-async-access-token-refresh--470t1c8
 */
const error = onError(({ graphQLErrors, networkError, operation, forward }: ErrorResponse) => {
  if (graphQLErrors) {
    const isOnRecommendedPage = window?.location.pathname.startsWith("/events/recommended");

    // Check for schema mismatch errors
    for (const error of graphQLErrors) {
      if (error?.message?.includes("Cannot query field") && reloadForSchemaMismatch()) {
        return;
      }
    }

    for (const error of graphQLErrors) {
      if (error?.message === "Unauthorized") {
        // Try refreshing access
        // biome-ignore lint/suspicious/noConfusingVoidType: it is what it is
        let forward$: Observable<boolean | void>;

        if (!isRefreshing) {
          isRefreshing = true;
          forward$ = fromPromise(
            getNewToken()
              .then((response) => {
                if (response.ok) {
                  resolvePendingRequests();
                  return true;
                }

                pendingRequests = [];
                // Handle token refresh errors e.g clear stored tokens, redirect to login, ...
                logout();
                return false;
              })
              .catch((error) => {
                console.error(error);
                pendingRequests = [];
                // Handle token refresh errors e.g clear stored tokens, redirect to login, ...
                logout();
              })
              .finally(() => {
                isRefreshing = false;
              }),
          ).filter((value) => Boolean(value));
        } else {
          // Will only emit once the Promise is resolved
          forward$ = fromPromise(
            new Promise<void>((resolve) => {
              pendingRequests.push(() => {
                resolve();
              });
            }),
          );
        }

        return forward$.flatMap(() => forward(operation));
      }
    }

    // If we are on the recommended events page and there is any error
    if (isOnRecommendedPage && graphQLErrors.length > 0) {
      window.location.replace("/events/all");
      return;
    }

    for (const error of graphQLErrors) {
      console.warn(error);
    }
  }
  if (networkError) {
    console.error(networkError);
    // window.location.replace("/heavy-load");
  }
});

const transport = split(
  (operation: Operation) => isSubscriptions(getMainDefinition(operation.query)),
  ws,
  http as any,
);

/**
 * Client
 */
const client = new ApolloClient({
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          ...notificationsFieldsPolicy,
          ...userHostedEventsFieldsPolicy,
        },
      },
    },
  }),
  connectToDevTools: import.meta.env.DEV,
  link: ApolloLink.from([retryLink, error, transport as any]),
});

const Apollo: React.FC<Props> = ({ children }: Props) => {
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default Apollo;
